import { clamp, isEnumValid } from '.';

export const EMPTY_COORD = { rowStart: 0, colStart: 0, rowEnd: 0, colEnd: 0 };

export enum TrackType {
  KEYWORD = 'keyword',
  LENGTH = 'length',
  FUNCTION = 'function',
}

export enum Unit {
  PX = 'px',
  VH = 'vh',
  VW = 'vw',
  EM = 'em',
  REM = 'rem',
  PERCENT = '%',
}

export enum Operator {
  DIVISION = '/',
}

export enum Keyword {
  AUTO = 'auto',
  MIN_CONTENT = 'min-content',
  MAX_CONTENT = 'max-content',
}

export enum Functions {
  MINMAX = 'minmax',
  CALC = 'calc'
}

export interface LengthTrack {
  type: TrackType.LENGTH;
  value: number;
  unit: Unit;
  toString(): string;
}

export interface KeywordTrack {
  type: TrackType.KEYWORD;
  unit: Keyword;
  toString(): string;
}

export interface FunctionTrack {
  type: TrackType.FUNCTION;
  name: string;
  params: AnyTrack[];
  toString(): string;
}

export interface Coord {
  rowStart: number;
  colStart: number;
  rowEnd: number;
  colEnd: number;
  toString(): string;
}

export interface Area {
  name: string;
  coord: Coord;
}

interface ObjectArea {
  [key: string]: Area;
}

interface LimitObject {
  [key: string]: {
    min: number;
    max: number;
  };
}

type AnyTrack = KeywordTrack | LengthTrack | FunctionTrack;

/**
 * Breaks the track list string (Ex gridTemplateColumns) into an array of track objects.
 *
 * @param trackList the track list string to be parsed
 */
function parseTrackList(trackListStr: string = ''): AnyTrack[] {
  const trackWordList = trackListStr.match(/([\w|.|-]+)/g) || [];
  const result = [];

  while (trackWordList.length) {
    const trackName = trackWordList.shift();

    const parsedTrack = parseTrack(trackName as string, trackWordList);
    result.push(parsedTrack);
  }

  return result;
}

/**
 * Receives an array of track objects and converts it back to string
 *
 * @param trackList the parsed track list
 */
function formatTrackList(trackList: AnyTrack[]): string {
  return trackList.join(' ');
}

/**
 * Removes a certain number of elements from the provided array
 *
 * @param list the array target to be manipulated
 * @param amount the amount of items to be shifted
 */
function shift(list: any[] = [], amount: number): any[] {
  const params = [];

  for (let i = 0; i < amount; i++) {
    params.push(list.shift());
  }

  return params;
}

/**
 * Based on the input provided by the parseTrackList method,
 * parses each of the tracks to its respective track objects.
 *
 * @param trackName the name used to identify the track object type
 * @param trackWordList the list of parameters within the track
 */
function parseTrack(trackName: string | AnyTrack, trackWordList?: string[]): AnyTrack {
  if (typeof trackName === 'object') {
    return trackName;
  }

  if (isEnumValid(Keyword, trackName) || isEnumValid(Operator, trackName)) {
    return parseKeyword(trackName as Keyword);
  }

  if (isEnumValid(Functions, trackName)) {
    if (trackName === Functions.MINMAX) {
      const params = shift(trackWordList, 2);
      return parseFunction(trackName, params);
    }

    if (trackName === Functions.CALC) {
      const params = shift(trackWordList, 3);
      return parseFunction(trackName, params, ' ');
    }
  }

  return parseLength(trackName);
}

/**
 * Parses track information into a FunctionTrack.
 *
 * @param name the track name
 * @param params the function parameters
 * @param seperator the symbol that separates each of the parameters when the track object is formatted.
 */
function parseFunction(name: string, params: any[], seperator?: string): FunctionTrack {
  const parsedParams = params.map(trackStr => parseTrack(trackStr));

  return {
    type: TrackType.FUNCTION,
    name,
    params: parsedParams,
    toString() {
      return `${this.name}(${this.params.join(seperator)})`;
    },
  };
}

/**
 * Parses track information into a KeywordTrack.
 *
 * @param value the keyword
 */
function parseKeyword(value: Keyword): KeywordTrack {
  return {
    type: TrackType.KEYWORD,
    unit: value,
    toString() {
      return this.unit;
    },
  };
}

/**
 * Parses track information into a LengthTrack.
 *
 * @param value the keyword
 */
function parseLength(valueStr: string = ''): LengthTrack {
  const [, value = 0, unit = 'px'] = String(valueStr).match(/([\d.]+)(\w*%*)/) || [];

  return {
    type: TrackType.LENGTH,
    value: Number(value),
    unit: unit as Unit,
    toString() {
      return `${this.value}${this.unit}`;
    },
  };
}

/**
 * Receives a grid template string (Ex.: gridTemplateAreas)
 * and returns an array of area objects.
 *
 * @param templateStr the grid template string
 */
function parseAreas(templateStr: string = ''): Area[] {
  const template = parseTemplate(templateStr);
  const areas: ObjectArea = {};

  for (let rowIndex = 0; rowIndex < template.length; rowIndex++) {
    const row = template[rowIndex];

    for (let colIndex = 0; colIndex < row.length; colIndex++) {
      const areaName = row[colIndex];

      if (areaName === '.') continue;

      if (areaName in areas) {
        areas[areaName].coord.rowEnd = rowIndex;
        areas[areaName].coord.colEnd = colIndex;
        continue;
      }

      areas[areaName] = {
        name: areaName,
        coord: {
          rowStart: rowIndex,
          rowEnd: rowIndex,
          colStart: colIndex,
          colEnd: colIndex,

          toString() {
            const { rowStart, colStart, rowEnd, colEnd } = this;
            return `${rowStart + 1} / ${colStart + 1} / ${rowEnd + 2} / ${colEnd + 2}`;
          },
        },
      };
    }
  }

  return Object.values(areas);
}

/**
 * Builds an empty template string array based on
 * the number of rows and columns provided
 *
 * @param rows the number of rows within the template
 * @param columns the number of columns within the template
 */
function buildEmptyTemplate(rows: number, columns: number): string[][] {
  const template = [];

  for (let i = 0; i < rows; i++) {
    template[i] = Array(columns).fill('.');
  }

  return template;
}

/**
 * Builds an template string array based on area objects.
 *
 * @param areas an array of parsed grid areas
 * @param rows the number of rows within the template
 * @param columns the number of columns within the template
 */
function buildTemplate(areas: Area[], rows: number, columns: number): string[][] {

  const template = buildEmptyTemplate(rows, columns);

  for (const area of areas) {
    const { rowStart, rowEnd, colStart, colEnd } = area.coord;

    const gridRowEnd = Math.min(rowEnd, rows - 1);
    const gridColEnd = Math.min(colEnd, columns - 1);

    for (let row = rowStart; row <= gridRowEnd; ++row) {
      for (let col = colStart; col <= gridColEnd; ++col) {
        template[row][col] = area.name;
      }
    }
  }

  return template;
}

/**
 * Receives a template string (Ex.: gridTemplateAreas)
 * and parses into an array of strings.
 *
 * @param template the template string
 */
function parseTemplate(template: string = ''): string[][] {
  if (!template) return [];

  return (
    template
      .substr(1, template.length - 2)
      .split('""')
      .map((r = '') => r.split(' '))
  );
}

/**
 * Coverts a template string array back to string
 *
 * @param template the template string array
 */
function formatTemplate(template: string[][]): string {
  return template.length ? `"${template.map(r => r.join(' ')).join('""')}"` : '';
}

/**
 * Receives a track object and scales down or up their value
 *
 * @param track the track object to be manipulated
 * @param scale the proportion which the value will be scaled
 * @param limits the minimum and maximum values allowed
 */
function scaleDownTracks(track: AnyTrack, scale: number = 1, limits: LimitObject = {}): AnyTrack {
  if (track.type === TrackType.FUNCTION) {
    const params = track.params.map(track => scaleDownTracks(track, scale, limits));
    const fnTrack: FunctionTrack = { ...track, params };
    return fnTrack;
  }

  if (track.type === TrackType.LENGTH
   && track.unit !== Unit.PERCENT && isEnumValid(Unit, track.unit)) {
    const limit = limits[track.unit] || { min: 0, max: Infinity };

    const value = clamp({
      value: track.value / scale,
      min: limit.min,
      max: limit.max,
    });

    const lengthTrack: LengthTrack = { ...track, value };

    return lengthTrack;
  }

  if (track.type === TrackType.KEYWORD && track.unit === Keyword.MIN_CONTENT) {
    return parseLength('30px');
  }

  return track;
}

/**
 * Receives two coordinates objects and merges them into one
 *
 * @param start the initial coordinate object
 * @param end the final coordinate object
 */
function mergeCoords(start: Coord, end: Coord): Coord {
  if (!start || !end) {
    return start || end || EMPTY_COORD;
  }

  const rowStart = Math.min(start.rowStart, end.rowStart);
  const colStart = Math.min(start.colStart, end.colStart);
  const rowEnd = Math.max(start.rowEnd, end.rowEnd);
  const colEnd = Math.max(start.colEnd, end.colEnd);

  return { rowStart, colStart, rowEnd, colEnd };
}

export {
  buildTemplate,
  formatTemplate,
  formatTrackList,
  mergeCoords,
  parseAreas,
  parseFunction,
  parseKeyword,
  parseLength,
  parseTemplate,
  parseTrack,
  parseTrackList,
  scaleDownTracks,
};

export default {
  buildTemplate,
  formatTemplate,
  formatTrackList,
  mergeCoords,
  parseAreas,
  parseFunction,
  parseKeyword,
  parseLength,
  parseTemplate,
  parseTrack,
  parseTrackList,
  scaleDownTracks,
};
